<template>
  <div>
    <v-list dense one-line v-if="documents[0]">
      <template v-for="(document, i) in documents">
        <v-subheader v-if="document.header" :key="`${i}-header`">
          {{ document.header }}
        </v-subheader>

        <v-divider
          v-else-if="document.divider"
          :inset="document.inset"
          :key="`${i}-divider`"
        ></v-divider>

        <v-list-tile v-else :key="`${i}-title`">
          <v-list-tile-avatar>
            <v-icon :color="document.color">{{ document.icon }}</v-icon>
          </v-list-tile-avatar>

          <v-list-tile-content v-if="typeof document.file === 'object'" :key="`${i}-attachment`">
            <DocumentPreview
              :title="document.title"
              :label="`${document.title}`"
              :file="document.file"
              :color="document.color"
              flat
            />
          </v-list-tile-content>
          <v-list-tile-content v-else :key="`${i}-missing-file`" class="pl-3">
            Missing documents for {{ document.title }}
          </v-list-tile-content>

          <v-list-tile-action
            v-if="
              application &&
                application.profile &&
                ((document.flags && document.flags[0]) ||
                  ($can('create', 'Flag') && document.target && document.filter))
            "
          >
            <FlagAction
              :filter="$can('create', 'Flag') ? document.filter : []"
              :flags="document.flags || []"
              :profile="$can('create', 'Flag') ? application.profile : null"
              :target="$can('create', 'Flag') ? document.target : null"
              @done="$emit('refresh')"
            />
          </v-list-tile-action>
        </v-list-tile>
      </template>
    </v-list>
    <v-subheader v-else>No documents available</v-subheader>
  </div>
</template>

<script>
// Components
import DocumentPreview from '@/components/DocumentPreview'
import FlagAction from '@/components/flags/FlagAction'

// Helpers
import _ from 'lodash'
import { byEndsAt, byLevels } from '@/sorting/ProfileQualification'
import { mode } from 'simple-statistics'

export default {
  components: { DocumentPreview, FlagAction },

  props: { application: Object },

  mounted() {
    this.resetDocuments()
  },

  data() {
    return {
      documents: [],
    }
  },
  computed: {
    flags() {
      if (!_.get(this, 'application.profile.flags[0]')) return []

      return _.get(this, 'application.profile.flags')
    },

    hasQualificationDocuments() {
      return this.qualifications.some(q => {
        return _.get(q, 'proofOfRegistration') || _.get(q, 'transcript')
      })
    },

    hasQualifications() {
      return _.get(this, 'qualifications[0]')
    },

    qualifications() {
      if (!_.get(this.application, 'profile.profileQualifications')) return []

      let qualifications = JSON.parse(
        JSON.stringify(this.application.profile.profileQualifications)
      )
      return qualifications
        .sort(byLevels({ country: this.levelsCountryMode(), asc: false }))
        .sort(byEndsAt({ asc: false }))
    },
  },

  watch: {
    application: {
      handler() {
        this.resetDocuments()
      },
      deep: true,
    },
  },

  methods: {
    flagsForDocument({ prefix, targetId, targetType }) {
      return this.flags.filter(
        f => f.targetType === targetType && f.targetId === targetId && f.key.indexOf(`${prefix}.`) === 0
      )
    },

    levelsCountryMode() {
      let qualifications = JSON.parse(
        JSON.stringify(this.application.profile.profileQualifications)
      )
      const countries = qualifications
        .filter(pq => _.get(pq, 'qualification.levels[0]'))
        .reduce(
          (acc, { qualification: { levels } }) => acc.concat(levels.map(l => l.countryCode)),
          []
        )
      return countries[0] ? mode(countries) : null
    },

    newTab(uri) {
      if (!uri) return

      window.open(uri, '_blank')
    },

    resetDocuments() {
      const id = _.get(this.application, 'profile.identityDocument')
      const profile = _.get(this, 'application.profile')
      this.documents = []

      if (id) {
        this.documents.push(
          { header: 'Personal Documents' },
          {
            color: 'secondary',
            file: id,
            filter: ['identity_document'],
            flags: this.flagsForDocument({
              prefix: 'identity_document',
              targetId: profile.id,
              targetType: 'Profile',
            }),
            icon: 'mdi-file-account',
            target: { ...profile, type: 'Profile' },
            title: 'Identification Document',
          }
        )
      }

      if (this.hasQualifications) this.documents.push({ header: 'Qualification Documents' })

      this.qualifications.forEach((q, i) => {
        let registration = _.get(q, 'proofOfRegistration')
        let transcript = _.get(q, 'transcript')

        if (transcript) {
          this.documents.push({
            icon: 'mdi-file-document',
            color: transcript ? 'secondary' : 'warning',
            filter: ['transcript'],
            title: `${q.qualificationTypeName} ${q.specialization}`,
            file: transcript,
            flags: this.flagsForDocument({
              prefix: 'transcript',
              targetId: q.id,
              targetType: 'ProfileQualification',
            }),
            target: { ...q, type: 'ProfileQualification' },
          })
        } else if (registration) {
          this.documents.push({
            icon: 'mdi-file-document',
            color: registration ? 'secondary' : 'warning',
            title: `${q.qualificationTypeName} ${q.specialization}`,
            file: registration,
          })
        } else {
          this.documents.push({
            icon: 'mdi-close-circle',
            color: 'warning',
            title: `${q.qualificationTypeName} ${q.specialization}`,
          })
        }
      })

      if (_.get(this, 'application.profile.financial.incomes[0]')) {
        this.documents.push({
          header: `Household Income (${this.application.profile.financial.dependents} dependents)`,
        })
        this.application.profile.financial.incomes.forEach(i => {
          if (i.proof) {
            this.documents.push({
              icon: 'mdi-file-document',
              color: 'secondary',
              title: `${i.relationship}: ${i.typeOfProof}`,
              file: i.proof,
              flags: this.flags.filter(
                f => f.targetType === 'HouseholdIncome' && f.targetId === i.id
              ),
            })
          }
        })
      }
    },
  },
}
</script>
